// ------------ import internal dependencies ------------
import { AUTH_API_TOKEN_KEY, setUserEmail } from "../../../api/authApi";

const GENERIC_MESSAGE = "Could not update the email. Nothing was changed.";
const UNCONFIRMED_MESSAGE =
  "We couldn't confirm the save. Check the email address and try again.";

/**
 * Carries the message in the shape the screens' existing catch blocks already read
 * (error.response.data.message), so a .NET refusal reaches the toast with no extra branch.
 */
export class EmailSyncError extends Error {
  response: { data: { message: string } };

  constructor(message: string) {
    super(message);
    this.name = "EmailSyncError";
    this.response = { data: { message } };
  }
}

const normalize = (email?: string | null) => (email ?? "").trim().toLowerCase();

export const hasEmailChanged = (
  previousEmail?: string | null,
  newEmail?: string | null
) => normalize(previousEmail) !== normalize(newEmail);

/** Axios errors carry request bodies and the Authorization header, so only the status is logged. */
const warn = (message: string, error?: any) => {
  const status = error?.response?.status;
  console.warn(
    typeof status === "number" ? `${message} (HTTP ${status})` : message
  );
};

/** Only a 4xx proves the server refused the write; a 5xx or no response may follow a commit. */
const isDefiniteRejection = (error: any) => {
  const status = error?.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
};

const serverMessage = (error: any) => {
  const fromServer = error?.response?.data?.error;
  return typeof fromServer === "string" && fromServer.length > 0
    ? fromServer
    : GENERIC_MESSAGE;
};

interface UpdateUserWithEmailSyncArgs<T> {
  salesforceContactId: string;

  /** What the portal currently holds for this user. */
  previousEmail?: string | null;

  /** Undefined means the email is not part of this save. */
  newEmail?: string | null;

  saveToSalesforce: () => Promise<T>;
}

/**
 * Saves a user update, keeping the .NET identity store in step when the email changes:
 * .NET first, then Salesforce, with a best-effort .NET rollback if Salesforce definitely
 * rejects (4xx) the save. Throws EmailSyncError when .NET refuses or when either outcome
 * is unknown (5xx or no response), and the Salesforce error unchanged otherwise.
 *
 * A blank new email is not synced here: Salesforce rejects it, and .NET must not be
 * cleared first. The bearer token is read up front because an invalid Salesforce session
 * clears localStorage, which would leave the rollback with no token.
 */
export const updateUserWithEmailSync = async <T>({
  salesforceContactId,
  previousEmail,
  newEmail,
  saveToSalesforce,
}: UpdateUserWithEmailSyncArgs<T>): Promise<T> => {
  if (
    newEmail === undefined ||
    normalize(newEmail) === "" ||
    !hasEmailChanged(previousEmail, newEmail)
  ) {
    return saveToSalesforce();
  }

  const token = localStorage.getItem(AUTH_API_TOKEN_KEY) ?? "";
  let dotNetUpdated = false;
  let restoreEmail = previousEmail;

  try {
    const response = await setUserEmail(
      { salesforceContactId, newEmail: (newEmail ?? "").trim() },
      token
    );
    dotNetUpdated = response.data?.updated === true;
    // .NET's own previous value is authoritative; the caller's snapshot is only a fallback.
    const serverPrevious = response.data?.previousEmail;
    if (typeof serverPrevious === "string" && serverPrevious.trim() !== "") {
      restoreEmail = serverPrevious;
    }
  } catch (error: any) {
    throw new EmailSyncError(
      isDefiniteRejection(error) ? serverMessage(error) : UNCONFIRMED_MESSAGE
    );
  }

  try {
    return await saveToSalesforce();
  } catch (error) {
    if (dotNetUpdated && !isDefiniteRejection(error)) {
      // Salesforce may have committed, and its trigger sync will heal .NET.
      warn(
        "Salesforce save outcome unknown; leaving the .NET email as updated"
      );
      throw new EmailSyncError(UNCONFIRMED_MESSAGE);
    }
    if (dotNetUpdated) {
      try {
        await setUserEmail(
          { salesforceContactId, newEmail: (restoreEmail ?? "").trim() },
          token
        );
      } catch (rollbackError) {
        warn(
          "Could not roll back the .NET email after a failed Salesforce save",
          rollbackError
        );
      }
    }
    throw error;
  }
};
